import { StateCreator } from "zustand";

import { Notification } from "models/Notification";
import { RootState } from "store";
import { NotificationsAPI } from "api/notifications";

export interface NotificationsSlice {
  messages: Notification[];
  isLoading: boolean;

  setLoadingNotifications: (isLoading: boolean) => void;
  dismissNotification: (id: string) => void;
  isNewPass: (isNew: boolean) => void;
  deleteNotification: (id: string) => void;
  sendSystemMessage: (message: string, hours: string) => void;
  fetchNotifications: () => void;
  addToCustomerResponseAccepted: (id: string) => void;
  addToCustomerResponseRejected: (id: string) => void;
  fetchSysMessage: () => void;
  sendSystemMessageExternal: (sysMessage: {
    message: string;
    hours: string;
  }) => void;
}

export const createNotificationsSlice: StateCreator<
  RootState,
  [["zustand/devtools", never]],
  [],
  NotificationsSlice
> = (set, get) => ({
  messages: [],
  isLoading: false,

  setLoadingNotifications: (isLoading) =>
    set(
      ({ notifications }) => ({
        notifications: {
          ...notifications,
          isLoading
        }
      }),
      false,
      "notifications/SET_LOADING_NOTIFICATIONS"
    ),

  dismissNotification: (id) =>
    set(
      ({ notifications }) => ({
        notifications: {
          ...notifications,
          messages: notifications.messages.filter(
            (message) => message.id !== id
          )
        }
      }),
      false,
      "notifications/DISMISS_NOTIFICATION"
    ),

  isNewPass: (isNew) => {
    set(
      (state) => ({
        user: {
          ...state.user,
          isNewPass: isNew
        }
      }),
      false,
      "notifications/IS_NEW_PASS"
    );
  },

  sendSystemMessage: async (message, hours) => {
    try {
      const result = await NotificationsAPI.sendSystemMessage(message, hours);
      set(
        ({ notifications }) => ({
          notifications: {
            ...notifications,
            messages: [...notifications.messages, result]
          }
        }),
        false,
        "notifications/ADD_SYSTEM_MESSAGE"
      );
    } catch (error) {
      get().error.appendErrorNotification(
        "Utskicket av systemmeddelande misslyckades.",
        error
      );
    }
  },

  deleteNotification: async (id: string) => {
    try {
      await NotificationsAPI.deleteNotification(id);
      set(
        ({ notifications }) => ({
          notifications: {
            ...notifications,
            messages: notifications.messages.filter((msg) => msg.id !== id)
          }
        }),
        false,
        "notifications/DELETE_NOTIFICATION"
      );
    } catch (error: any) {
      get().error.appendErrorNotification(
        "Borttagning av aviseringen misslyckades.",
        error
      );
    }
  },

  fetchNotifications: async () => {
    get().notifications.setLoadingNotifications(true);
    try {
      const response = await NotificationsAPI.fetchNotifications();
      set(
        ({ notifications }) => ({
          notifications: {
            ...notifications,
            messages: response
          }
        }),
        false,
        "notifications/FETCH_NOTIFICATIONS"
      );
    } catch (error) {
      get().error.appendErrorNotification(
        "Hämtning av avisering misslyckades.",
        error
      );
    } finally {
      get().notifications.setLoadingNotifications(false);
    }
  },

  addToCustomerResponseAccepted: async (id) => {
    try {
      await NotificationsAPI.addToCustomerResponseAccepted(id);
    } catch (error) {
      get().error.appendErrorNotification(
        "Systemet kunde inte bekräfta aviseringen.",
        error
      );
    } finally {
      get().notifications.dismissNotification(id);
    }
  },

  addToCustomerResponseRejected: async (id) => {
    try {
      await NotificationsAPI.addToCustomerResponseRejected(id);
    } catch (error) {
      get().error.appendErrorNotification(
        "Avböjande av aviseringen misslyckades.",
        error
      );
    } finally {
      get().notifications.dismissNotification(id);
    }
  },

  fetchSysMessage: async () => {
    try {
      const sysMessage = await NotificationsAPI.fetchSysMessage();
      if (sysMessage) {
        set(
          (state) => ({
            user: {
              ...state.user,
              sysMessage
            }
          }),
          false,
          "notifications/GOT_SYS_MESSAGE"
        );
      }
    } catch (error) {
      const errMessage = "Serveranslutning misslyckades. Nytt försök kan göras vid ett senare tillfälle.";
      set(
        (state) => ({
          user: {
            ...state.user,
            sysMessage:{
              message: errMessage,
            }
          }
        }),
        false,
        "notifications/GOT_SYS_MESSAGE"
      );
      get().error.appendErrorNotification(
        "Hämtning av systemmeddelande misslyckades.",
        error
      );
    }
  },

  sendSystemMessageExternal: async (sysMessage) => {
    try {
      const sysMessageResult =
        await NotificationsAPI.sendSystemMessageExternal(sysMessage);
      if (sysMessageResult) {
        set(
          (state) => ({
            user: {
              ...state.user,
              sysMessage: sysMessageResult
            }
          }),
          false,
          "notifications/SEND_SYSTEM_MESSAGE_EXTERNAL"
        );
      }
    } catch (error) {
      get().error.appendErrorNotification(
        "Systemet kunde inte leverera systemmeddelandet till den externa användaren.",
        error
      );
    }
  }
});
